import React from 'react';
import { Link } from 'react-router-dom';

function LegalLayout({ eyebrow, title, updated, children }) {
  return (
    <div>
      <section className="relative py-20 md:py-24 border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 viso-grid opacity-40" />
        <div className="relative max-w-4xl mx-auto px-6">
          <div className="text-xs font-mono uppercase tracking-[0.35em] text-cyan-300 mb-4">{eyebrow}</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-[1.1]">
            <span className="text-gradient">{title}</span>
          </h1>
          <div className="text-sm text-slate-400 mt-5">Last updated: {updated}</div>
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 legal-body space-y-10">
          {children}
        </div>
      </section>
      <section className="border-t border-white/5 py-10">
        <div className="max-w-4xl mx-auto px-6 text-sm text-slate-400 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div>Questions about this document? Reach us via <Link to="/contact" className="text-cyan-300">the contact page</Link>.</div>
          <div className="flex gap-4 text-xs">
            <Link to="/legal/privacy" className="hover:text-cyan-300">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-cyan-300">Terms</Link>
            <Link to="/legal/security" className="hover:text-cyan-300">Security</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section>
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-300">Section {n}</div>
      <h2 className="font-display text-2xl md:text-3xl font-bold mt-2 mb-4">{title}</h2>
      <div className="space-y-3 text-slate-300 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="space-y-2 mt-2">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-3 text-slate-300">
          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-cyan-300 flex-shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

export function PrivacyPage() {
  return (
    <LegalLayout eyebrow="Legal · Privacy" title="Privacy Policy" updated="01 July 2025">
      <p className="text-slate-300 leading-relaxed">
        Visotonics (“<strong>Visotonics</strong>”, “we”, “us” or “our”) is the operator of the Visotonics platform, the website at visotonics.com and related services (collectively, the “Services”). This Privacy Policy explains what personal data we collect, how we use it, who we share it with, and the choices and rights you have.
      </p>

      <Section n="01" title="Who we are">
        <p>The data controller responsible for your personal data is <strong>Visotonics</strong>, having its registered office in Lucknow, India. All matters relating to this Privacy Policy are governed exclusively by the laws of India and are subject to the jurisdiction of the courts in Lucknow, India.</p>
      </Section>

      <Section n="02" title="Data we collect">
        <p>We collect personal data that you provide to us and data that we generate through your use of the Services:</p>
        <Bullets items={[
          'Identity data — name, business title, employer / company name.',
          'Contact data — company email address, telephone number, business address.',
          'Account data — login credentials (in hashed form), role (user, partner, administrator), and preferences.',
          'Usage data — pages visited, features used, session duration, device identifiers and IP address.',
          'Deal & commercial data — information submitted by partners in deal-registration forms.',
          'Operational data — telemetry, event metadata and analytics derived from customer camera feeds where a customer has authorised such processing.',
        ]} />
        <p>We do <strong>not</strong> knowingly collect data from children. The Services are intended for business use only.</p>
      </Section>

      <Section n="03" title="How we use your data">
        <p>We use personal data to:</p>
        <Bullets items={[
          'Provide, secure and operate the Services;',
          'Authenticate users, enforce role-based access, and manage the partner and deal-registration programme;',
          'Respond to enquiries, provide customer support, and send service communications;',
          'Improve product performance, model accuracy, and customer experience;',
          'Comply with legal obligations and enforce our contractual rights.',
        ]} />
      </Section>

      <Section n="04" title="Legal basis for processing">
        <p>Where required by law, we rely on one or more of the following legal bases: (a) performance of a contract with you or your organisation; (b) our legitimate interests in running and improving the Services; (c) compliance with a legal obligation; and (d) your consent, where applicable.</p>
      </Section>

      <Section n="05" title="How we share your data">
        <p>We do not sell personal data. We share personal data only with:</p>
        <Bullets items={[
          'Sub-processors and hosting providers strictly under contract to operate the Services;',
          'Professional advisers (legal, audit, accounting) under confidentiality obligations;',
          'Authorities and regulators where disclosure is required by applicable law;',
          'A successor entity in the event of a business reorganisation, always subject to equivalent protections.',
        ]} />
      </Section>

      <Section n="06" title="Data retention">
        <p>We retain personal data only for as long as required to provide the Services, comply with our legal obligations, resolve disputes and enforce our agreements. When no longer required, personal data is deleted or anonymised.</p>
      </Section>

      <Section n="07" title="Security">
        <p>We apply administrative, technical and physical safeguards designed to protect personal data against unauthorised access, alteration, disclosure or destruction. For further detail, please see our <Link to="/legal/security" className="text-cyan-300">Security Policy</Link>.</p>
      </Section>

      <Section n="08" title="International transfers">
        <p>Where personal data is transferred outside India, such transfers are made only to jurisdictions that offer adequate protection or under appropriate contractual safeguards.</p>
      </Section>

      <Section n="09" title="Your rights">
        <p>Subject to applicable law, you may request to access, correct, delete or restrict the processing of your personal data, or object to certain processing. To exercise these rights, contact us using the details below.</p>
      </Section>

      <Section n="10" title="Cookies">
        <p>We use only strictly necessary and analytics cookies. Analytics cookies are enabled only with your consent and can be withdrawn at any time from your browser settings.</p>
      </Section>

      <Section n="11" title="Consent">
        <p>By using the Services, you consent to this Privacy Policy and to the collection, use and disclosure of your personal data as described herein. Where required by applicable law, we will seek your explicit consent before processing certain categories of data, and you may withdraw that consent at any time by contacting us.</p>
      </Section>

      <Section n="12" title="Tracking & cookies data">
        <p>In addition to cookies, we may use similar tracking technologies such as web beacons, pixel tags and local storage to collect and track information about your use of the Services and to improve and analyse them. You can instruct your browser to refuse cookies or to indicate when a cookie is being sent; if you do not accept cookies, some parts of the Services may be unavailable.</p>
      </Section>

      <Section n="13" title="Do Not Track">
        <p>Some browsers include a “Do Not Track” (DNT) feature. Because there is no common industry standard for DNT, we currently do not respond to DNT signals, but we will continue to review our practices as standards develop.</p>
      </Section>

      <Section n="14" title="Behavioral advertising">
        <p>We do not currently use your personal data to serve targeted behavioural advertising. If this changes, we will update this Policy and, where required, provide an opt-out mechanism and honour applicable industry opt-out programmes.</p>
      </Section>

      <Section n="15" title="Data retention">
        <p>We retain personal data only for as long as required to provide the Services, comply with our legal obligations, resolve disputes and enforce our agreements. When no longer required, personal data is deleted or anonymised.</p>
      </Section>

      <Section n="16" title="Appointment of grievance / privacy compliance officer">
        <p>In accordance with applicable data-protection law, Visotonics has appointed a Grievance / Privacy Compliance Officer responsible for addressing privacy-related grievances and queries. The Grievance / Privacy Compliance Officer can be reached via <Link to="/contact" className="text-cyan-300">the contact page</Link>, and we will acknowledge and address grievances within the timeframes prescribed by applicable law.</p>
      </Section>

      <Section n="17" title="Casual visitors note">
        <p>If you are a casual visitor who browses the website without registering for or otherwise using the Services, we collect only limited data (such as usage and cookie data described above) and do not require you to provide identity or account data.</p>
      </Section>

      <Section n="18" title="Changes to this policy">
        <p>We may update this Privacy Policy from time to time. The “Last updated” date at the top of this page indicates when it was last revised.</p>
      </Section>

      <Section n="19" title="Contact & jurisdiction">
        <p>For any privacy-related enquiry, please contact Visotonics at its registered office in Lucknow, India via <Link to="/contact" className="text-cyan-300">the contact page</Link>. This Privacy Policy is governed by the laws of India, and the courts of Lucknow, India shall have exclusive jurisdiction over any dispute arising from it.</p>
      </Section>
    </LegalLayout>
  );
}

export function TermsPage() {
  return (
    <LegalLayout eyebrow="Legal · Terms" title="Terms of Service" updated="01 July 2025">
      <p className="text-slate-300 leading-relaxed">
        These Terms of Service (“<strong>Terms</strong>”) govern your access to and use of the Visotonics platform, the website at visotonics.com and all related services (collectively, the “Services”) provided by <strong>Visotonics</strong> (“Visotonics”, “we”, “us” or “our”), a company having its registered office in Lucknow, India. By accessing or using the Services you agree to be bound by these Terms.
      </p>

      <Section n="01" title="Acceptance">
        <p>By registering for or using the Services, you represent that you are authorised to bind your organisation and that you accept these Terms on its behalf. If you do not agree, do not access or use the Services.</p>
      </Section>

      <Section n="02" title="Accounts & registration">
        <p>You must provide accurate information at registration and keep it current. You are responsible for the confidentiality of your credentials and for all activities performed under your account. Access is limited to authorised representatives of your organisation.</p>
      </Section>

      <Section n="03" title="Partner programme & deal registration">
        <p>Partners may register deals through the partner portal. All deals are subject to review and approval by Visotonics in accordance with the partner programme guidelines. Registration of a deal does not, by itself, create any exclusivity or entitlement to commission.</p>
      </Section>

      <Section n="04" title="Acceptable use">
        <p>You agree not to:</p>
        <Bullets items={[
          'Use the Services in violation of any applicable law or third-party right;',
          'Reverse-engineer, decompile or disassemble any component of the Services;',
          'Attempt to gain unauthorised access to the Services or interfere with their operation;',
          'Upload malicious code, or content that is unlawful, infringing or offensive;',
          'Resell, sublicense or otherwise commercially exploit the Services except as expressly permitted in writing.',
        ]} />
      </Section>

      <Section n="05" title="Intellectual property">
        <p>The Services, including all software, models, documentation, trademarks and content, are the exclusive property of Visotonics or its licensors and are protected by intellectual-property law. Except for the limited right of use granted under these Terms, no rights are transferred to you.</p>
      </Section>

      <Section n="06" title="Customer data">
        <p>You retain all rights in the data you or your organisation submits to the Services (“Customer Data”). You grant Visotonics a limited, non-exclusive right to process Customer Data solely to provide, secure and improve the Services in accordance with our Privacy Policy.</p>
      </Section>

      <Section n="07" title="Fees & payment">
        <p>Fees, if any, are set out in the applicable order form or commercial proposal. Unless otherwise stated, all fees are exclusive of applicable taxes and are non-refundable once paid.</p>
      </Section>

      <Section n="08" title="Disclaimers">
        <p>The Services are provided on an “as is” and “as available” basis. To the maximum extent permitted by law, Visotonics disclaims all warranties, whether express, implied or statutory, including warranties of merchantability, fitness for a particular purpose and non-infringement.</p>
      </Section>

      <Section n="09" title="Limitation of liability">
        <p>To the maximum extent permitted by law, in no event shall Visotonics be liable for any indirect, incidental, special, consequential, punitive or exemplary damages, or for loss of profits, revenue, goodwill or data, arising out of or related to the Services. Our aggregate liability under these Terms shall not exceed the amount paid by you (if any) for the Services in the twelve (12) months preceding the event giving rise to liability.</p>
      </Section>

      <Section n="10" title="Indemnity">
        <p>You agree to indemnify and hold Visotonics harmless from any third-party claim arising out of your breach of these Terms or your unlawful use of the Services.</p>
      </Section>

      <Section n="11" title="Term & termination">
        <p>These Terms remain in effect until terminated. Visotonics may suspend or terminate your access to the Services at any time for material breach or for a security or legal reason. Provisions that by their nature should survive termination shall so survive.</p>
      </Section>

      <Section n="12" title="Privacy & data protection">
        <p>Your use of the Services is also governed by our <Link to="/legal/privacy" className="text-cyan-300">Privacy Policy</Link>, which describes how we collect, use and protect personal data. By accepting these Terms, you also acknowledge the Privacy Policy.</p>
      </Section>

      <Section n="13" title="Delivery policy">
        <p>Where the Services include delivery of digital deliverables, reports, licence keys or hardware, delivery timelines will be as agreed in the applicable order form or commercial proposal. Estimated delivery dates are indicative and not guaranteed unless expressly agreed in writing.</p>
      </Section>

      <Section n="14" title="Optional tools">
        <p>We may provide access to third-party tools or integrations over which we have no control. Any such optional tools are provided “as is” and “as available”, without warranties of any kind, and your use of them is at your own risk and subject to the relevant third party’s terms.</p>
      </Section>

      <Section n="15" title="Online store terms">
        <p>Where any part of the Services allows online purchase of products, subscriptions or licences, you agree that all purchases are subject to acceptance, product descriptions and pricing may change without notice, and we reserve the right to limit quantities or refuse any order at our discretion.</p>
      </Section>

      <Section n="16" title="General investment disclosure">
        <p>Nothing on the Services constitutes financial, investment or legal advice, or an offer or solicitation to buy or sell any security or investment. Any commercial, partnership or deal-registration information is provided for general informational purposes only, and you should seek independent professional advice before making any investment or commercial decision.</p>
      </Section>

      <Section n="17" title="Grievance officer">
        <p>In accordance with applicable law, Visotonics has appointed a Grievance Officer to address complaints or concerns regarding these Terms or the Services. The Grievance Officer can be contacted via <Link to="/contact" className="text-cyan-300">the contact page</Link> and will acknowledge and address grievances within the timeframes prescribed by applicable law.</p>
      </Section>

      <Section n="18" title="Waiver and severability">
        <p>No failure or delay by Visotonics in exercising any right under these Terms shall operate as a waiver of that right. If any provision of these Terms is held invalid or unenforceable, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.</p>
      </Section>

      <Section n="19" title="Severability">
        <p>If any provision of these Terms is found by a court of competent jurisdiction to be invalid, the parties nevertheless agree that the court should endeavour to give effect to the parties’ intentions as reflected in the provision, and the other provisions of these Terms shall remain in full force and effect.</p>
      </Section>

      <Section n="20" title="Entire agreement">
        <p>These Terms, together with the Privacy Policy, Security Policy and any applicable order form, constitute the entire agreement between you and Visotonics regarding the Services and supersede any prior or contemporaneous agreements, communications or proposals, whether oral or written.</p>
      </Section>

      <Section n="21" title="Governing law & jurisdiction">
        <p>These Terms are governed by, and construed in accordance with, the laws of India. Any dispute arising out of or in connection with these Terms shall be subject to the <strong>exclusive jurisdiction of the competent courts in Lucknow, India</strong>.</p>
      </Section>

      <Section n="22" title="Changes to these Terms">
        <p>We may modify these Terms from time to time. Continued use of the Services after such changes constitutes acceptance of the revised Terms.</p>
      </Section>
    </LegalLayout>
  );
}

export function SecurityPage() {
  return (
    <LegalLayout eyebrow="Legal · Security" title="Security Policy" updated="01 July 2025">
      <p className="text-slate-300 leading-relaxed">
        Visotonics designs, builds and operates its platform with a defence-in-depth security posture. This Security Policy summarises the administrative, technical and physical controls implemented to protect the confidentiality, integrity and availability of the Services and the data processed on them. It is intended for information purposes and does not modify the contractual commitments contained in a signed order form or master services agreement.
      </p>

      <Section n="01" title="Governance">
        <Bullets items={[
          'Documented information-security programme owned by the leadership team and reviewed at least annually.',
          'Policies aligned with recognised frameworks including ISO/IEC 27001 and SOC 2 principles.',
          'Security responsibilities assigned across the organisation with defined incident escalation.',
        ]} />
      </Section>

      <Section n="02" title="People & personnel">
        <Bullets items={[
          'Background verification for all employees prior to access provisioning.',
          'Annual mandatory security and privacy training for staff.',
          'Confidentiality obligations enforced through employment and contractor agreements.',
        ]} />
      </Section>

      <Section n="03" title="Access control">
        <Bullets items={[
          'Role-based access control (RBAC) with least-privilege by default.',
          'Multi-factor authentication (MFA) required for all administrative access.',
          'Passwords stored using industry-standard salted, one-way hashing (bcrypt).',
          'Access reviews conducted on a periodic basis and on role change or termination.',
        ]} />
      </Section>

      <Section n="04" title="Data protection">
        <Bullets items={[
          'Encryption in transit using TLS 1.2 or higher for all external and administrative traffic.',
          'Encryption at rest for production databases and backups using AES-256 (or equivalent).',
          'Key management performed via managed cloud key services with restricted operator access.',
          'Data segregation between customer tenants enforced at the application and infrastructure layers.',
        ]} />
      </Section>

      <Section n="05" title="Application security">
        <Bullets items={[
          'Secure software development lifecycle (SSDLC) with code review and static analysis on every change.',
          'Dependency and container-image scanning on the build pipeline.',
          'Regular vulnerability scanning and periodic third-party penetration testing.',
          'Defence-in-depth against the OWASP Top 10 including input validation, output encoding and CSRF/XSS mitigations.',
        ]} />
      </Section>

      <Section n="06" title="Infrastructure & network">
        <Bullets items={[
          'Deployment in hardened, network-isolated environments with default-deny firewall policies.',
          'Managed VPCs with dedicated subnets and security groups per service tier.',
          'DDoS protection at the ingress layer.',
          'Segregation of production from development and staging environments.',
        ]} />
      </Section>

      <Section n="07" title="Logging & monitoring">
        <Bullets items={[
          'Centralised, tamper-evident logging of authentication, administrative and application events.',
          'Continuous monitoring with automated alerting on anomalous behaviour.',
          'Log retention aligned to legal and contractual obligations.',
        ]} />
      </Section>

      <Section n="08" title="Incident response">
        <Bullets items={[
          'Documented incident-response plan with defined severity, triage and communication procedures.',
          'Customer notification of confirmed security incidents in accordance with the applicable contract and law.',
          'Post-incident review and remediation tracking.',
        ]} />
      </Section>

      <Section n="09" title="Business continuity & backups">
        <Bullets items={[
          'Automated encrypted backups of critical data with defined retention.',
          'Documented recovery-time and recovery-point objectives (RTO / RPO).',
          'Periodic testing of restore procedures.',
        ]} />
      </Section>

      <Section n="10" title="Vendor management">
        <Bullets items={[
          'Due diligence performed on all sub-processors and critical vendors.',
          'Written data-processing agreements imposing equivalent security obligations.',
          'Ongoing monitoring of vendor security posture.',
        ]} />
      </Section>

      <Section n="11" title="Physical security">
        <p>All production infrastructure runs on facilities operated by leading cloud providers, which maintain ISO 27001, SOC 2 and equivalent certifications for physical, environmental and personnel controls.</p>
      </Section>

      <Section n="12" title="Responsible disclosure">
        <p>We welcome reports from independent security researchers. If you believe you have identified a vulnerability affecting the Services, please contact us via <Link to="/contact" className="text-cyan-300">the contact page</Link>. We will acknowledge the report, investigate promptly and coordinate any disclosure.</p>
      </Section>

      <Section n="13" title="Governing law & jurisdiction">
        <p>This Security Policy is governed by the laws of India. Any dispute arising out of or in connection with this Policy shall be subject to the exclusive jurisdiction of the competent courts in Lucknow, India.</p>
      </Section>
    </LegalLayout>
  );
}
